import React, { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send } from "lucide-react";
import { API } from "@/lib/api";

const STARTER = "Hello! I'm Atelier, your SquireCouture concierge. Ask me about fabrics, fit, or style for your next look.";

export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", content: STARTER }]);
  const [input, setInput] = useState("");
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setInput("");
    setLoading(true);
    try {
      const res = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text, session_id: session }),
      });
      if (!res.ok || !res.body) throw new Error("Chat unavailable");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (line.startsWith("event: done")) {
            const sid = line.split("data:")[1]?.trim();
            if (sid) setSession(sid);
          } else if (line.startsWith("data:")) {
            const chunk = line.slice(5).replace(/^ /, "");
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + chunk };
              return copy;
            });
          }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "I had trouble connecting. Please try again in a moment." };
        return copy;
      });
    }
    setLoading(false);
  };

  return (
    <>
      <button
        data-testid="chatbot-toggle"
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-6 right-8 z-[60] w-12 h-12 bg-squire-jeans hover:bg-black text-white flex items-center justify-center transition-colors shadow-xl rounded-full"
        aria-label="Open chat"
      >
        {open ? <X size={20} /> : <MessageCircle size={20} />}
      </button>
      {open && (
        <div className="fixed bottom-20 right-8 z-[60] w-[360px] max-w-[92vw] h-[520px] bg-white border border-black shadow-2xl flex flex-col" data-testid="chatbot-panel">
          <div className="bg-black text-white px-5 py-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-squire-jeans flex items-center justify-center font-display font-bold">A</div>
            <div>
              <div className="font-display text-lg leading-none">Atelier</div>
              <div className="sq-eyebrow text-white/60 text-[10px] mt-1">Online · Concierge</div>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto sq-scroll p-4 space-y-3 bg-[#FDFDFD]">
            {messages.map((m, i) => (
              <div key={i} className={`max-w-[85%] text-sm leading-relaxed ${m.role === "user" ? "ml-auto bg-squire-jeans text-white" : "bg-white border border-black/10"} px-4 py-3`}>
                {m.content || <span className="opacity-50">…</span>}
              </div>
            ))}
            <div ref={endRef} />
          </div>
          <div className="flex items-center gap-2 p-3 border-t border-black/10">
            <input
              data-testid="chatbot-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask about fabrics, fit, occasion…"
              className="flex-1 bg-transparent outline-none text-sm border-b border-black/20 focus:border-squire-jeans py-2"
            />
            <button data-testid="chatbot-send" onClick={send} disabled={loading} className="bg-squire-jeans text-white p-2 disabled:opacity-50">
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}